import { OverridableComponent, OverrideProps } from "@material-ui/core/OverridableComponent";
import LessIcon from "@material-ui/icons/ExpandLessRounded";
import MoreIcon from "@material-ui/icons/ExpandMoreRounded";
import clsx from "clsx";
import React, { useCallback, useRef } from "react";
import { useBooleanState } from "../../hook";
import { ifDefined, neverReachedFor } from "../../util";
import { PopupMenu } from "../popupMenu";
import { Button, ButtonTypeMap, ButtonVariant } from "./Button";

export interface DropdownButtonTypeMap<PropsT = unknown, ComponentT extends React.ElementType = "button"> {
    props: Omit<ButtonTypeMap<PropsT, ComponentT>["props"], "endIcon" | "variant"> & {
        /**
         * Denote the menu items to be shown. They are wrapped into a single `MenuList`.
         */
        children: React.ReactNode;
        /**
         * Buttons label is not provided as `children` as they are used for list of `menuItems`.
         */
        label: React.ReactNode;
        variant: ButtonVariant.Contextual | ButtonVariant.Page | ButtonVariant.Table;
    };
    defaultComponent: ButtonTypeMap<PropsT, ComponentT>["defaultComponent"];
    classKey: ButtonTypeMap<PropsT, ComponentT>["classKey"];
}

export type DropdownButtonProps<ComponentT extends React.ElementType = DropdownButtonTypeMap["defaultComponent"], PropsT = unknown> = OverrideProps<
    DropdownButtonTypeMap<PropsT, ComponentT>,
    ComponentT
>;

export const DropdownButton = React.forwardRef(
    <ComponentT extends React.ElementType, PropsT>(
        { children, className, label, onClick, variant, ...props }: DropdownButtonProps<ComponentT, PropsT>,
        ref: DropdownButtonProps["ref"],
    ) => {
        // We need the `ref` internally for the `PopupMenu` but also want to provide `ref` to the outside if required
        const innerRef = useRef<HTMLButtonElement>();
        const refCb = useCallback(
            (element) => {
                ifDefined(ref, (definedRef) =>
                    typeof definedRef === "function" ? definedRef(element) : ((definedRef as React.MutableRefObject<HTMLButtonElement>).current = element),
                );
                innerRef.current = element;
            },
            [ref, innerRef],
        );

        const [open, hide, show] = useBooleanState(false);

        const handleClick = useCallback(
            (event: React.MouseEvent<HTMLButtonElement>) => {
                open ? hide() : show();
                onClick?.(event);
            },
            [open, hide, show],
        );

        return (
            <>
                <Button
                    className={clsx(className, { "Mui-focusVisible": open })} // reuse Material UIs `.Mui-focusVisible` pseudo class as styling is equal
                    endIcon={variant === ButtonVariant.Contextual ? undefined : open ? <LessIcon /> : <MoreIcon />}
                    onClick={handleClick}
                    ref={refCb}
                    variant={variant}
                    {...props}
                >
                    {label}
                </Button>
                <PopupMenu
                    anchorEl={innerRef?.current}
                    open={open}
                    onClose={hide}
                    placement={
                        variant === ButtonVariant.Page
                            ? "top-start"
                            : variant === ButtonVariant.Contextual || variant === ButtonVariant.Table
                            ? "bottom-start"
                            : neverReachedFor(variant)
                    }
                >
                    {children}
                </PopupMenu>
            </>
        );
    },
) as OverridableComponent<DropdownButtonTypeMap>;
